import { sanitize } from '@core/shared';
import { useTranslation } from 'react-i18next';
import type { PlatformNotice } from '../../hooks/platformStatus';
import { usePlatformStatus } from '../../hooks/usePlatformStatus';
import { Warning, WarningOutlined } from '../Icons';
import type { RouteArea } from './routeArea';

export { getRouteArea, isPlayerPage, type RouteArea } from './routeArea';

// One platform-wide notice paints every tenant origin, and its endpoint is writable from inside the
// cluster without a credential, so the body keeps only what an operator actually needs: emphasis, a
// line break and a link to a status page. DOMPurify's default profile would also let a form, an
// image or an iframe through.
const NOTICE_HTML = { ALLOWED_TAGS: ['b', 'strong', 'i', 'em', 'u', 'br', 'a'], ALLOWED_ATTR: ['href'] };

/** Current UI language first, then English, then whatever the operator did fill in. */
export const selectNoticeMessage = (message: Record<string, string>, language: string): string => {
    const short = language.split('-')[0];
    const candidates = [message[language], message[short], message['en'], ...Object.values(message)];
    return candidates.find((candidate) => typeof candidate === 'string' && candidate.trim().length > 0) ?? '';
};

/** General pages show every scope; a viewer page shows its own scope or `all`. */
export const noticeAppliesTo = (notice: PlatformNotice, area: RouteArea): boolean => area === 'general' || notice.scope === 'all' || notice.scope === area;

export const MaintenanceNotice = ({ area, gatewayOverlay = true }: { area: RouteArea; gatewayOverlay?: boolean }) => {
    const { t, i18n } = useTranslation();
    const status = usePlatformStatus();

    if (status.kind === 'gateway-maintenance') {
        // The caller has its own overlay — ConnectionOfflineOverlay on the viewer pages.
        if (!gatewayOverlay) {
            return null;
        }
        return (
            <div data-testid="maintenance-gateway" role="alert" className="fixed inset-0 z-50 flex items-center justify-center bg-primary/95 p-6">
                <div className="max-w-xl bg-white rounded-md px-6 py-5 text-center">
                    <WarningOutlined classNames="size-10 fill-warning mb-2 mx-auto" />
                    <p className="text-2xl mb-2">{t('Maintenance.Gateway.Title')}</p>
                    <p>{t('Maintenance.Gateway.Body')}</p>
                </div>
            </div>
        );
    }

    if (status.kind !== 'notice' || !noticeAppliesTo(status.notice, area)) {
        return null;
    }

    const text = sanitize(selectNoticeMessage(status.notice.message, i18n.language), NOTICE_HTML);
    if (!text) {
        return null;
    }
    const tone = status.notice.severity === 'warning' ? 'bg-warning-light' : 'bg-accent-light';
    return (
        <div data-testid="maintenance-notice" role="status" className={`flex items-center ${tone} pl-3 pr-4 py-2`}>
            <Warning classNames="size-6 flex-none -mt-0.5 mr-2" />
            <span className="font-bold mr-2">{t('Maintenance.Notice.Title')}</span>
            <span className="inline" dangerouslySetInnerHTML={{ __html: text }}></span>
        </div>
    );
};
